import { z } from 'zod';
import { ProfileAvatarDesign } from '../members/avatar.js';
import { COMMENT_MAX_BLOCKS, COMMENT_MAX_INLINE_HTML_LEN, COMMENT_MAX_PLAINTEXT_LEN, ImageCommentBlock, ParagraphCommentBlock, QuoteRefCommentBlock, } from '../primitives/editor-block.js';
const ForumCodeAttributes = z
    .object({
    language: z.string().max(64).optional(),
    showLineNumbers: z.boolean().optional(),
    wordWrap: z.boolean().optional(),
    theme: z.enum(['light', 'dark']).optional(),
    tabSize: z.number().int().min(1).max(8).optional(),
    useTabs: z.boolean().optional(),
})
    .strict();
export const CodeForumBlock = z
    .object({
    id: z.string().min(1).max(64),
    type: z.literal('code'),
    content: z.string().max(COMMENT_MAX_INLINE_HTML_LEN),
    html: z.string().max(COMMENT_MAX_INLINE_HTML_LEN).optional(),
    attributes: ForumCodeAttributes,
    children: z.array(z.never()).max(0).optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
const ForumMathAttributes = z
    .object({
    latex: z.string().max(COMMENT_MAX_PLAINTEXT_LEN).optional(),
    displayMode: z.boolean().optional(),
})
    .strict();
export const MathForumBlock = z
    .object({
    id: z.string().min(1).max(64),
    type: z.literal('math'),
    content: z.string().max(COMMENT_MAX_PLAINTEXT_LEN),
    html: z.string().max(COMMENT_MAX_INLINE_HTML_LEN).optional(),
    attributes: ForumMathAttributes,
    children: z.array(z.never()).max(0).optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const ForumBlock = z.discriminatedUnion('type', [
    ParagraphCommentBlock,
    QuoteRefCommentBlock,
    ImageCommentBlock,
    CodeForumBlock,
    MathForumBlock,
]);
export const ForumBlocks = z.array(ForumBlock).min(1).max(COMMENT_MAX_BLOCKS);
export const DOMEditorForumBlock = z.lazy(() => z
    .object({
    id: z.string().min(1).max(64),
    type: z.string().min(1).max(32),
    attrs: z.record(z.string(), z.unknown()),
    content: z.array(z.unknown()).optional(),
    children: z.array(DOMEditorForumBlock).optional(),
})
    .passthrough());
export const DOMEditorForumBody = z
    .object({
    version: z.literal(2),
    blocks: z.array(DOMEditorForumBlock).min(1).max(COMMENT_MAX_BLOCKS),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
export const ForumBody = z.union([ForumBlocks, DOMEditorForumBody]);
export const ForumThreadKind = z.enum([
    'discussion',
    'question',
    'announcement',
    'survey-link',
    'poll',
    'ama',
    'event-rsvp',
]);
export const ForumThreadState = z.enum(['open', 'locked', 'solved', 'hidden', 'deleted']);
export const ForumSortOrder = z.enum(['recent', 'newest', 'top']);
export const ForumTranslationLocale = z.enum(['zh', 'en']);
export const ForumTranslationSourceLocale = z.enum(['zh', 'en', 'mixed']);
export const ForumTranslationStatus = z.enum(['pending', 'completed', 'failed']);
export const ForumTranslationSummary = z.object({
    targetLocale: ForumTranslationLocale,
    sourceLocale: ForumTranslationSourceLocale,
    status: ForumTranslationStatus,
    updatedAt: z.string().datetime({ offset: true }),
    completedAt: z.string().datetime({ offset: true }).nullable(),
});
export const ForumThreadEventDetails = z.object({
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }).nullable(),
    allDay: z.boolean(),
    tzid: z.string(),
    location: z.string().nullable(),
    rsvpCapacity: z.number().int().min(1).max(10000).nullable(),
    rsvpWaitlistEnabled: z.boolean(),
});
export const ForumCategory = z.object({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    position: z.number().int(),
    readOnly: z.boolean().optional(),
    moderatorOnly: z.boolean().optional(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    viewerCanManage: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const ForumAttachment = z.object({
    fileId: z.string().uuid(),
    name: z.string().min(1).max(255),
    url: z
        .string()
        .min(1)
        .max(2048)
        .refine((value) => {
        try {
            const parsed = new URL(value);
            return parsed.protocol === 'http:' || parsed.protocol === 'https:';
        }
        catch {
            return value.startsWith('/api/');
        }
    }, 'expected http(s) URL or API content path'),
    mimeType: z.string().min(1).max(128),
    sizeBytes: z.number().int().nonnegative(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    alt: z.string().max(200).optional(),
});
export const ForumPost = z.object({
    id: z.string().uuid(),
    threadId: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    depth: z.number().int().min(0).max(8),
    authorId: z.string().uuid().nullable(),
    authorDisplayName: z.string().nullable(),
    authorAvatarDesign: ProfileAvatarDesign.nullable().optional(),
    body: ForumBody,
    bodyText: z.string(),
    mentions: z.array(z.string().uuid()),
    attachments: z.array(ForumAttachment),
    score: z.number().int(),
    viewerVote: z.union([z.literal(-1), z.literal(1)]).nullable(),
    accepted: z.boolean(),
    hidden: z.boolean(),
    deleted: z.boolean(),
    viewerCanWrite: z.boolean().nullable(),
    translations: z.array(ForumTranslationSummary),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    editedAt: z.string().datetime({ offset: true }).nullable(),
});
export const ForumThread = z.object({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    groupIds: z.array(z.string().uuid()),
    categoryId: z.string().uuid().nullable(),
    kind: ForumThreadKind,
    title: z.string(),
    summary: z.string(),
    body: ForumBody,
    bodyText: z.string(),
    authorId: z.string().uuid().nullable(),
    authorDisplayName: z.string().nullable(),
    authorAvatarDesign: ProfileAvatarDesign.nullable().optional(),
    state: ForumThreadState,
    pinned: z.boolean(),
    tags: z.array(z.string()),
    event: ForumThreadEventDetails.nullable(),
    acceptedPostId: z.string().uuid().nullable(),
    postCount: z.number().int().nonnegative(),
    score: z.number().int(),
    subscribed: z.boolean().nullable(),
    viewerCanWrite: z.boolean().nullable(),
    viewerCanReply: z.boolean(),
    viewerCanModerate: z.boolean(),
    translations: z.array(ForumTranslationSummary),
    lastPostAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const ForumThreadTranslation = z.object({
    threadId: z.string().uuid(),
    targetLocale: ForumTranslationLocale,
    sourceLocale: ForumTranslationSourceLocale,
    status: ForumTranslationStatus,
    title: z.string(),
    summary: z.string(),
    body: ForumBody,
    bodyText: z.string(),
    provider: z.string().nullable(),
    model: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    completedAt: z.string().datetime({ offset: true }).nullable(),
});
export const ForumPostTranslation = z.object({
    postId: z.string().uuid(),
    threadId: z.string().uuid(),
    targetLocale: ForumTranslationLocale,
    sourceLocale: ForumTranslationSourceLocale,
    status: ForumTranslationStatus,
    body: ForumBody,
    bodyText: z.string(),
    provider: z.string().nullable(),
    model: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    completedAt: z.string().datetime({ offset: true }).nullable(),
});
export const ForumThreadDetail = ForumThread.extend({
    posts: z.array(ForumPost),
});
export const ForumPollOption = z.object({
    id: z.string().uuid(),
    threadId: z.string().uuid(),
    label: z.string(),
    position: z.number().int(),
    voteCount: z.number().int().nonnegative(),
});
export const ForumPoll = z.object({
    threadId: z.string().uuid(),
    options: z.array(ForumPollOption),
    viewerOptionId: z.string().uuid().nullable(),
    totalVotes: z.number().int().nonnegative(),
});
export const ForumEventRsvpStatus = z.enum(['going', 'maybe', 'declined', 'waitlisted']);
export const ForumEventRsvpSummary = z.object({
    threadId: z.string().uuid(),
    viewerStatus: ForumEventRsvpStatus.nullable(),
    capacity: z.number().int().min(1).max(10000).nullable(),
    seatsRemaining: z.number().int().nonnegative().nullable(),
    waitlistEnabled: z.boolean(),
    counts: z.object({
        going: z.number().int().nonnegative(),
        maybe: z.number().int().nonnegative(),
        declined: z.number().int().nonnegative(),
        waitlisted: z.number().int().nonnegative(),
    }),
});
export const ForumRevision = z.object({
    id: z.string().uuid(),
    subjectKind: z.enum(['thread', 'post']),
    subjectId: z.string().uuid(),
    threadId: z.string().uuid(),
    actorId: z.string().uuid().nullable(),
    revisionNo: z.number().int().positive(),
    title: z.string().nullable(),
    body: ForumBody,
    bodyText: z.string(),
    tags: z.array(z.string()),
    attachments: z.array(ForumAttachment),
    createdAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=forum.js.map